/**
 * Interface for Direct Message Service
 * Defines the contract for DM operations in Znoospace
 */
export class IDirectMessageService {
    /**
     * Send a direct message to another user
     * @param {string} recipientWallet - Recipient's wallet address
     * @param {string} content - Message content
     * @param {string} messageType - Type: 'text', 'image', 'gif'
     * @param {string} mediaUrl - Optional media URL for images/gifs
     * @returns {Promise<{success: boolean, transactionId?: string, error?: string}>}
     */
    async sendMessage(recipientWallet, content, messageType = 'text', mediaUrl = null) {
        throw new Error('sendMessage method must be implemented');
    }

    /**
     * Get conversation between current user and another user
     * @param {string} otherUserWallet - Other user's wallet address
     * @param {number} limit - Number of messages to retrieve
     * @param {string} before - Cursor for pagination
     * @returns {Promise<{success: boolean, messages?: Array, hasMore?: boolean, error?: string}>}
     */
    async getConversation(otherUserWallet, limit = 20, before = null) {
        throw new Error('getConversation method must be implemented');
    }

    /**
     * Get list of all conversations for current user
     * @param {number} limit - Number of conversations to retrieve
     * @returns {Promise<{success: boolean, conversations?: Array, error?: string}>}
     */
    async getConversationList(limit = 50) {
        throw new Error('getConversationList method must be implemented');
    }

    /**
     * Mark messages as read in a conversation
     * @param {string} otherUserWallet - Other user's wallet address
     * @param {Array<string>} messageIds - Array of message IDs to mark as read
     * @returns {Promise<{success: boolean, error?: string}>}
     */
    async markMessagesAsRead(otherUserWallet, messageIds) {
        throw new Error('markMessagesAsRead method must be implemented');
    }

    /**
     * Get unread message count for current user
     * @returns {Promise<{success: boolean, count?: number, error?: string}>}
     */
    async getUnreadCount() {
        throw new Error('getUnreadCount method must be implemented');
    }

    /**
     * Delete a message (soft delete - mark as deleted)
     * @param {string} messageId - Message ID to delete
     * @returns {Promise<{success: boolean, error?: string}>}
     */
    async deleteMessage(messageId) {
        throw new Error('deleteMessage method must be implemented');
    }

    /**
     * Search messages in conversations
     * @param {string} query - Search query
     * @param {string} conversationWith - Optional: limit search to specific conversation
     * @returns {Promise<{success: boolean, messages?: Array, error?: string}>}
     */
    async searchMessages(query, conversationWith = null) {
        throw new Error('searchMessages method must be implemented');
    }

    /**
     * Block/unblock a user from sending DMs
     * @param {string} userWallet - User wallet to block/unblock
     * @param {boolean} block - true to block, false to unblock
     * @returns {Promise<{success: boolean, error?: string}>}
     */
    async setUserBlocked(userWallet, block) {
        throw new Error('setUserBlocked method must be implemented');
    }

    /**
     * Get list of blocked users
     * @returns {Promise<{success: boolean, blockedUsers?: Array, error?: string}>}
     */
    async getBlockedUsers() {
        throw new Error('getBlockedUsers method must be implemented');
    }

    /**
     * Check if current user can send DM to target user
     * @param {string} targetWallet - Target user's wallet
     * @returns {Promise<{success: boolean, canSend?: boolean, reason?: string, error?: string}>}
     */
    async canSendMessageTo(targetWallet) {
        throw new Error('canSendMessageTo method must be implemented');
    }
}
